import type React from 'react';
import { useState } from 'react';

import { useGetEvent, useGetFaces } from '@vlabs/api/luna-api/client';
import type { EventId } from '@vlabs/api/luna-api/model/EventId';
import type { EventMatchResultPropertyItem } from '@vlabs/api/luna-api/model/EventMatchResultPropertyItem';
import type { FaceId } from '@vlabs/api/luna-api/model/FaceId';
import type { Similarity } from '@vlabs/api/luna-api/model/Similarity';
import { Button } from '@vlabs/uikit/components/controls/button/Button';

import { CardCard } from '@vlabs/monokit/shared/ui/card-card';

import st from './AllMatches.module.sass';
import { useAllMatchesTranslation } from './i18n';

interface AllMatchesPointProps {
  eventId: EventId;
  canReadLists?: boolean;
  canViewListDetails?: boolean;
}

const PAGE_SIZE = 8;

interface MatchItem {
  id: FaceId;
  label: string;
  similarity: Similarity;
}

function getMatchItems(matchResult?: EventMatchResultPropertyItem[] | null): MatchItem[] {
  return matchResult
    ?.flatMap(({ candidates, label }) => candidates
      .map((candidate): MatchItem | null => {
        if ('face' in candidate && candidate.face.face_id) {
          return {
            id: candidate.face.face_id,
            label,
            similarity: candidate.similarity,
          };
        }

        return null;
      }))
    .filter((match): match is MatchItem => match !== null)
    ?? [];
}

function AllMatchesPoint({
  eventId,
  canReadLists = true,
  canViewListDetails = true,
}: AllMatchesPointProps): React.JSX.Element | null {
  const { t } = useAllMatchesTranslation();
  const { data: event } = useGetEvent(eventId);
  const [visibleCount, setVisibleCount] = useState(PAGE_SIZE);

  const matches = getMatchItems(event?.match_result);
  // external_id у лица мог появиться позже, поэтому берем актуальные данные по face_id
  const { data: faces } = useGetFaces(
    { face_ids: matches.map(({ id }) => id), targets: ['face_id', 'external_id'] },
    { query: { enabled: matches.length > 0 } },
  );
  const cardFaceIds = faces?.faces
    ?.filter(({ external_id }) => !!external_id)
    .map(({ face_id }) => face_id) ?? [];
  const cardMatches = matches.filter(({ id }) => cardFaceIds.includes(id));
  const visibleMatches = cardMatches.slice(0, visibleCount);

  // полностью скрываем блок, если нет ни одного лица с external_id
  if (cardMatches.length === 0) return null;

  return (
    <div className={st.Root}>
      <h5>{t('title')}</h5>
      <div className={st.List}>
        {visibleMatches.map((match) => (
          <CardCard
            canReadLists={canReadLists}
            canViewListDetails={canViewListDetails}
            faceId={match.id}
            key={`${match.label}-${match.id}`}
            similarity={match.similarity}
          />
        ))}
      </div>
      {visibleCount < cardMatches.length && (
        <Button
          kind="primary"
          onClick={() => setVisibleCount((count) => count + PAGE_SIZE)}
          size="small"
          variant="outlined"
        >
          {t('show_more')}
        </Button>
      )}
    </div>
  );
}

export { AllMatchesPoint };
